import { type Duration, set, startOfTomorrow } from 'date-fns'
import { useState } from 'react'

import type { TaskResponse } from '@snootsvet/shared'

import { DatePicker } from '@/frontend/components/date-picker'
import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import { useUpdateTask } from '@/frontend/features/tasks/use-update-task'

const SNOOZE_PRESETS: Duration[] = [{ days: 1 }, { days: 2 }, { weeks: 1 }]

/** Tasks wake at the start of the working day, so a snooze "until Friday" is back on Friday's list. */
const WAKE_HOUR = 8

export function SnoozeTaskDialog({
  task,
  open,
  onOpenChange,
}: {
  task: TaskResponse
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { mutate, isPending, error } = useUpdateTask(task.id)
  const [date, setDate] = useState<Date>()

  const snooze = () => {
    if (!date) return
    const until = set(date, { hours: WAKE_HOUR, minutes: 0, seconds: 0, milliseconds: 0 })
    mutate({ snoozedUntil: until.toISOString() }, { onSuccess: () => onOpenChange(false) })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Snooze task</DialogTitle>
          <DialogDescription>
            Hide &ldquo;{task.title}&rdquo; from the task list until the morning of the chosen day.
          </DialogDescription>
        </DialogHeader>
        <DatePicker
          value={date}
          onChange={setDate}
          fromDate={startOfTomorrow()}
          presets={SNOOZE_PRESETS}
        />
        {error && <p className="text-sm text-destructive-fg">{error.message}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!date || isPending} onClick={snooze}>
            Snooze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
